<template>
  <div class="plans-page">
    <section class="plans-hero">
      <span class="plans-page-tag">
        <v-icon size="13" color="#0277BD">mdi-crown-outline</v-icon>
        {{ $t('upgradeToPlus') }}
      </span>
      <h1 class="plans-page-title">{{ $t('plansAndPrices') }}</h1>
      <p class="plans-page-subtitle">{{ $t('plansAndPricesDescription') }}</p>
    </section>

    <section class="plans-ladder" v-if="loading">
      <v-skeleton-loader type="image, article" v-for="i in 4" :key="`ladder-loading-${i}`" class="mb-8"/>
    </section>
    <section class="plans-ladder" v-else>
      <div v-for="(plan, index) in plansByPrice" :key="plan.id" class="plan-tier" :class="{ 'plan-tier-current': isCurrentPlan(plan) }">
        <v-row align="center">
          <v-col cols="12" md="6" :class="index % 2 === 1 ? 'order-1 order-md-2' : ''">
            <div class="plan-tier-mock reveal" :style="{ '--reveal-index': 0, background: tierBg(index), color: tierAccent(index) }" aria-hidden="true">
              <span class="plan-tier-mock-current" v-if="isCurrentPlan(plan)">
                <v-icon size="13" color="#FFFFFF">mdi-check-circle</v-icon>
                {{ $t('currentPlan') }}
              </span>
              <div class="plan-tier-mock-icon" :style="{ background: index === 0 ? '#E3F1FA' : 'rgba(255,255,255,0.18)' }">
                <v-icon size="24" :color="tierAccent(index)">{{ index === 0 ? 'mdi-gift-outline' : 'mdi-crown' }}</v-icon>
              </div>
              <div class="plan-tier-mock-name">{{ $t(`plan_${plan.name}`) }}</div>
              <div class="plan-tier-mock-price">
                R$<b>{{ formatPrice(plan.price).split(',')[0] }}</b>,{{ formatPrice(plan.price).split(',')[1] }}
                <span class="plan-tier-mock-price-period">/{{ $t('month') }}</span>
              </div>
              <div class="plan-tier-mock-badges">
                <span class="plan-tier-mock-badge" v-for="f in plan.subscriptionPlanIncludes.slice(0, 6)" :key="f.id" :style="{ background: index === 0 ? '#FFFFFF' : 'rgba(255,255,255,0.16)' }">
                  <v-icon size="16" :color="tierAccent(index)">{{ featureIcon(f.name) }}</v-icon>
                </span>
              </div>
              <div class="plan-tier-mock-trial" v-if="index > 0 && !isCurrentPlan(plan)" :style="trialBadgeStyle(index)">
                <v-icon size="13" :color="tierAccent(index)">mdi-clock-outline</v-icon>
                {{ $t('tryPlan') }}
              </div>
            </div>
          </v-col>
          <v-col cols="12" md="6" :class="index % 2 === 1 ? 'order-2 order-md-1' : ''">
            <span class="promo-plan-tag reveal" style="--reveal-index: 0" :class="{ 'promo-plan-tag-current': isCurrentPlan(plan) }">
              <v-icon size="13" :color="isCurrentPlan(plan) ? '#0277BD' : '#00A89F'">{{ isCurrentPlan(plan) ? 'mdi-check-circle' : 'mdi-layers-triple-outline' }}</v-icon>
              {{ isCurrentPlan(plan) ? $t('currentPlan') : $t(`plan_${plan.name}`) }}
            </span>
            <h2 class="section-title reveal" style="--reveal-index: 1">
              {{ ladderTitle(index) }}
            </h2>
            <p class="section-text mt-4 reveal" style="--reveal-index: 2" v-if="index > 0">
              {{ $t('trialPeriod') }}
            </p>
            <ul class="promo-list mt-6">
              <li
                v-for="(f, fi) in incrementalFeatures(index)"
                :key="f.id"
                class="reveal"
                :class="{ 'promo-list-item-link': isCurrentPlan(plan) && featureRoute(f.name) }"
                :style="{ '--reveal-index': fi + 3 }"
                @click="isCurrentPlan(plan) && featureRoute(f.name) ? $router.push(featureRoute(f.name)) : null"
              >
                <v-icon size="18" color="#0277BD">mdi-check-circle</v-icon>
                <span>{{ $t(f.name) }}</span>
              </li>
            </ul>
            <button
              type="button"
              class="plan-tier-cta reveal"
              :style="{ '--reveal-index': incrementalFeatures(index).length + 3 }"
              v-if="index > 0 && !isCurrentPlan(plan)"
              @click="selectPlan(plan)"
            >
              <v-icon size="16" color="#FFFFFF" class="mr-1">mdi-crown</v-icon>
              {{ $t('assignPlan').replace('{plan}', $t(`plan_${plan.name}`)) }}
            </button>
          </v-col>
        </v-row>
      </div>
    </section>

    <section class="plans-filter" v-if="!loading">
      <div class="plans-filter-header">
        <span class="promo-plan-tag reveal" style="--reveal-index: 0">
          <v-icon size="13" color="#00A89F">mdi-tune-variant</v-icon>
          {{ $t('planFilterTag') }}
        </span>
        <h2 class="section-title reveal" style="--reveal-index: 1">{{ $t('planFilterTitle') }}</h2>
        <p class="section-text mt-4 reveal" style="--reveal-index: 2">{{ $t('planFilterSubtitle') }}</p>
      </div>
      <div class="plans-filter-body">
        <div class="plans-filter-switches">
          <div class="plans-filter-switch-item reveal" v-for="(f, fi) in allFeatures" :key="f.name" :style="{ '--reveal-index': fi + 3 }">
            <v-switch v-model="neededFeatures[f.name]" inset hide-details color="#0277BD" class="ma-0 pa-0">
              <template v-slot:label>
                <span class="plans-filter-switch-label">
                  <v-icon size="16" color="#0277BD" class="mr-2">{{ featureIcon(f.name) }}</v-icon>
                  {{ $t(f.name) }}
                </span>
              </template>
            </v-switch>
          </div>
        </div>
        <div class="plans-filter-result">
          <template v-if="!selectedFeatureCount">
            <v-icon size="32" color="#B7C2D9">mdi-cursor-default-click-outline</v-icon>
            <p class="plans-filter-empty">{{ $t('planFilterEmptyState') }}</p>
          </template>
          <template v-else-if="recommendedPlan">
            <span class="plans-filter-result-label">{{ $t('planFilterRecommendedLabel') }}</span>
            <div class="plans-filter-result-icon">
              <v-icon size="26" color="#0277BD">{{ recommendedPlan.price === 0 ? 'mdi-gift-outline' : 'mdi-crown' }}</v-icon>
            </div>
            <div class="plans-filter-result-name">{{ $t(`plan_${recommendedPlan.name}`) }}</div>
            <div class="plans-filter-result-price">
              R$<b>{{ formatPrice(recommendedPlan.price).split(',')[0] }}</b>,{{ formatPrice(recommendedPlan.price).split(',')[1] }}
              <span class="plan-tier-mock-price-period">/{{ $t('month') }}</span>
            </div>
            <button type="button" class="plan-tier-cta mt-4" v-if="recommendedPlan.price > 0 && !isCurrentPlan(recommendedPlan)" @click="selectPlan(recommendedPlan)">
              <v-icon size="16" color="#FFFFFF" class="mr-1">mdi-crown</v-icon>
              {{ $t('assignPlan').replace('{plan}', $t(`plan_${recommendedPlan.name}`)) }}
            </button>
            <span class="plans-filter-current-badge" v-else-if="isCurrentPlan(recommendedPlan)">
              <v-icon size="14" color="#0277BD">mdi-check-circle</v-icon>
              {{ $t('currentPlan') }}
            </span>
          </template>
        </div>
      </div>
    </section>

    <section class="plans-testimonial-section">
      <div class="testimonial-card reveal" style="--reveal-index: 0">
        <p class="testimonial-quote">&ldquo;{{ $t('home.pricingTestimonialQuote') }}&rdquo;</p>
        <div class="testimonial-author">
          <picture>
            <source srcset="/images/person/001-88.webp" type="image/webp"/>
            <img src="/images/person/001-88.jpg" alt="Luis Eduardo" loading="lazy" width="44" height="44" class="testimonial-avatar"/>
          </picture>
          <div>
            <div class="testimonial-name">Luis Eduardo</div>
            <div class="testimonial-role">{{ $t('home.roleDeveloper') }}</div>
            <a href="https://www.instagram.com/lss.duda/" target="_blank" rel="noopener" class="testimonial-social">@lss.duda</a>
          </div>
        </div>
      </div>
    </section>

    <section class="plans-carousel-section">
      <div class="plans-skin">
        <SubscriptionAndPlans ref="subscriptionAndPlans" :screenData="screenData" :planId="planId"/>
      </div>
    </section>
  </div>
</template>
<script>
import SubscriptionAndPlans from "../fragments/SubscriptionAndPlans.vue";
import { apiV1SubscriptionPlan } from "../../../service/resources/api-v1-subscription-plan";

  export default {
    name: 'PainelPlans',
    props: ['screenData'],
    components: {
      SubscriptionAndPlans,
    },
    data: () => ({
      loading: false,
      plans: {content: []},
      revealObserver: null,
      neededFeatures: {},
    }),
    mounted: function() {
      this.loading = true
      apiV1SubscriptionPlan.page({
        page: 0,
        size: 100,
        active: true,
        embedSubscriptionPlanInclude: true,
      }).then(result => {
        this.plans = result.data
        this.loading = false
        this.allFeatures.forEach(f => this.$set(this.neededFeatures, f.name, false))
        this.$nextTick(() => this.setupRevealObserver())
      })
    },
    beforeDestroy: function() {
      if (this.revealObserver) this.revealObserver.disconnect()
    },
    computed: {
      planId: function() {
        return this.$route.query.planId || null
      },
      plansByPrice: function() {
        return this.plans.content.slice().sort((a, b) => a.price - b.price)
      },
      allFeatures: function() {
        const seen = new Set()
        const list = []
        this.plansByPrice.forEach(plan => {
          plan.subscriptionPlanIncludes.forEach(f => {
            if (!seen.has(f.name)) {
              seen.add(f.name)
              list.push(f)
            }
          })
        })
        return list
      },
      selectedFeatureCount: function() {
        return Object.values(this.neededFeatures).filter(Boolean).length
      },
      recommendedPlan: function() {
        const needed = Object.keys(this.neededFeatures).filter(name => this.neededFeatures[name])
        if (!needed.length) return null
        return this.plansByPrice.find(plan => {
          const names = plan.subscriptionPlanIncludes.map(f => f.name)
          return needed.every(n => names.includes(n))
        }) || null
      },
    },
    methods: {
      formatPrice: function(value) {
        let val = (value/1).toFixed(2).replace('.', ',')
        return val.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".")
      },
      featureIcon: function(name) {
        const desc = this.$t(`desc_${name}`)
        return (desc && typeof desc === 'object' && desc.icons && desc.icons[0]) || 'mdi-check-circle'
      },
      featureRoute: function(name) {
        return ({ INFOPRODUCT: '/painel/infoproduct' })[name] || null
      },
      incrementalFeatures: function(index) {
        const plan = this.plansByPrice[index]
        if (!plan) return []
        if (index === 0) return plan.subscriptionPlanIncludes
        const prev = this.plansByPrice[index - 1]
        const prevNames = new Set(prev.subscriptionPlanIncludes.map(f => f.name))
        const diff = plan.subscriptionPlanIncludes.filter(f => !prevNames.has(f.name))
        return diff.length ? diff : plan.subscriptionPlanIncludes
      },
      ladderTitle: function(index) {
        if (index === 0)
          return this.$t('plansLadderBaseTitle').replace('{plan}', this.$t(`plan_${this.plansByPrice[0].name}`))
        return this.$t('plansLadderMoreTitle').replace('{plan}', this.$t(`plan_${this.plansByPrice[index - 1].name}`))
      },
      tierBg: function(index) {
        return ['#FFFFFF', '#E3F1FA', '#0277BD', '#0277BD'][index] || '#0277BD'
      },
      tierAccent: function(index) {
        return index <= 1 ? '#0277BD' : '#FFFFFF'
      },
      isCurrentPlan: function(plan) {
        if (!this.screenData || !this.screenData.auth || !this.screenData.auth.user) return false
        const userPlans = this.screenData.auth.user.userHasSubscriptionPlans
        const paidPlans = userPlans.filter(v => !v.subscriptionPlan.free)
        if (paidPlans.length)
          return paidPlans.some(v => v.subscriptionPlan.id == plan.id)
        return plan.price === 0
      },
      trialBadgeStyle: function(index) {
        return index === 1
          ? { background: 'rgba(2, 119, 189, 0.12)', color: '#0277BD' }
          : { background: 'rgba(255,255,255,0.18)', color: '#FFFFFF' }
      },
      selectPlan: function(plan) {
        this.$refs.subscriptionAndPlans.getPlan(plan)
      },
      setupRevealObserver: function() {
        const prefersReducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
        const elements = this.$el.querySelectorAll ? this.$el.querySelectorAll('.reveal') : []
        if (prefersReducedMotion || !window.IntersectionObserver) {
          elements.forEach(el => el.classList.add('is-visible'))
          return
        }
        this.revealObserver = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-visible')
              this.revealObserver.unobserve(entry.target)
            }
          })
        }, { threshold: 0.2 })
        elements.forEach(el => this.revealObserver.observe(el))
      },
    },
  }
</script>
<style scoped>
.plans-page {
  font-family: 'Montserrat', sans-serif;
  background: #FFFFFF;
  border-radius: 1.25rem;
  padding: 2.5rem;
}
@media (max-width: 600px) {
  .plans-page {
    padding: 1.5rem 1rem;
  }
}
.plans-hero {
  margin-bottom: 3rem;
  max-width: 640px;
}
.plans-page-tag {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #E3F1FA;
  color: #0277BD;
  font-weight: 700;
  font-size: 0.75rem;
  padding: 0.35rem 0.85rem;
  border-radius: 999px;
  margin-bottom: 0.9rem;
}
.plans-page-title {
  font-weight: 900;
  font-size: 2rem;
  color: #101827;
  margin: 0 0 0.75rem;
}
.plans-page-subtitle {
  color: #5B6472;
  font-size: 1rem;
  line-height: 1.7;
  margin: 0;
}

.plans-ladder {
  margin-bottom: 4rem;
}
.plan-tier {
  padding: 2.5rem 0;
  border-top: 1px solid rgba(16,24,39,0.08);
}
.plan-tier:first-child {
  border-top: none;
}
.plan-tier-current {
  background: #F7F5EF;
  border-radius: 1.5rem;
  padding: 2.5rem;
  border-top: none;
}

.plan-tier-mock {
  position: relative;
  border-radius: 1.5rem;
  padding: 2rem;
  text-align: center;
  box-shadow: 0 20px 40px rgba(16,24,39,0.08);
  max-width: 340px;
  margin: 0 auto;
}
.plan-tier-current .plan-tier-mock {
  box-shadow: 0 0 0 3px #0277BD, 0 20px 40px rgba(16,24,39,0.08);
}
.plan-tier-mock-current {
  position: absolute;
  top: -13px;
  left: 50%;
  transform: translateX(-50%);
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 0.3rem 0.9rem;
  border-radius: 999px;
  background: #0277BD;
  color: #FFFFFF;
  font-weight: 700;
  font-size: 0.68rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  white-space: nowrap;
  box-shadow: 0 6px 14px rgba(2, 119, 189, 0.35);
}
.promo-plan-tag-current {
  background: #E3F1FA !important;
  color: #0277BD !important;
}
.plan-tier-mock-icon {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1rem;
}
.plan-tier-mock-name {
  font-weight: 800;
  font-size: 1.15rem;
  text-transform: lowercase;
}
.plan-tier-mock-price {
  font-weight: 900;
  font-size: 1.6rem;
  margin-top: 0.35rem;
}
.plan-tier-mock-price-period {
  font-weight: 500;
  font-size: 0.85rem;
  opacity: 0.75;
}
.plan-tier-mock-badges {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 1.4rem;
}
.plan-tier-mock-badge {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}
.plan-tier-mock-trial {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin-top: 1.4rem;
  padding: 0.35rem 0.8rem;
  border-radius: 999px;
  background: rgba(255,255,255,0.18);
  color: #FFFFFF;
  font-weight: 700;
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.plans-filter {
  margin: 4rem 0;
  padding: 2.5rem;
  background: #F7F5EF;
  border-radius: 1.5rem;
}
.plans-filter-header {
  max-width: 560px;
  margin-bottom: 2rem;
}
.plans-filter-body {
  display: grid;
  grid-template-columns: 1fr 300px;
  gap: 2rem;
  align-items: start;
}
.plans-filter-switches {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.5rem 1.5rem;
}
.plans-filter-switch-item {
  background: #FFFFFF;
  border-radius: 1rem;
  padding: 0.4rem 1rem;
}
.plans-filter-switch-label {
  display: inline-flex;
  align-items: center;
  color: #101827;
  font-weight: 600;
  font-size: 0.88rem;
}
.plans-filter-result {
  position: sticky;
  top: 1rem;
  background: #FFFFFF;
  border-radius: 1.25rem;
  padding: 2rem 1.5rem;
  text-align: center;
  box-shadow: 0 20px 40px rgba(16,24,39,0.08);
}
.plans-filter-empty {
  color: #7E8EA6;
  font-size: 0.88rem;
  margin: 0.75rem 0 0;
}
.plans-filter-result-label {
  display: inline-block;
  color: #00A89F;
  font-weight: 700;
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 0.75rem;
}
.plans-filter-result-icon {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: #E3F1FA;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 0.75rem;
}
.plans-filter-result-name {
  font-weight: 800;
  font-size: 1.2rem;
  color: #101827;
  text-transform: lowercase;
}
.plans-filter-result-price {
  font-weight: 900;
  font-size: 1.5rem;
  color: #0277BD;
  margin-top: 0.35rem;
}
.plans-filter-current-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 1.25rem;
  padding: 0.35rem 0.9rem;
  border-radius: 999px;
  background: #E3F1FA;
  color: #0277BD;
  font-weight: 700;
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}
@media (max-width: 960px) {
  .plans-filter-body {
    grid-template-columns: 1fr;
  }
  .plans-filter-switches {
    grid-template-columns: 1fr;
  }
  .plans-filter-result {
    position: static;
  }
}

.plans-carousel-section {
  margin-top: 2rem;
}

.plans-testimonial-section {
  max-width: 560px;
  margin: 2.5rem auto 0;
}
.testimonial-card {
  background: #FFFFFF;
  border: 1px solid rgba(16,24,39,0.06);
  border-radius: 1.25rem;
  padding: 1.75rem;
  box-shadow: 0 20px 40px rgba(16,24,39,0.06);
  text-align: left;
}
.testimonial-quote {
  color: #101827;
  font-size: 1rem;
  line-height: 1.6;
  margin-bottom: 1.25rem;
}
.testimonial-author {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}
.testimonial-avatar {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  object-fit: cover;
  flex: none;
}
.testimonial-name {
  font-weight: 700;
  color: #101827;
  font-size: 0.9rem;
}
.testimonial-role {
  color: #5B6472;
  font-size: 0.8rem;
}
.testimonial-social {
  display: inline-block;
  color: #0277BD;
  font-size: 0.78rem;
  font-weight: 600;
  text-decoration: none;
}
.testimonial-social:hover {
  text-decoration: underline;
}

/* Scroll reveal (respects prefers-reduced-motion) */
.reveal {
  opacity: 0;
  transform: translateY(24px);
  transition: opacity 0.6s cubic-bezier(0.16,1,0.3,1), transform 0.6s cubic-bezier(0.16,1,0.3,1);
  transition-delay: calc(var(--reveal-index, 0) * 90ms);
}
.reveal.is-visible {
  opacity: 1;
  transform: translateY(0);
}
@media (prefers-reduced-motion: reduce) {
  .reveal {
    transition: none !important;
    opacity: 1 !important;
    transform: none !important;
  }
}

.promo-plan-tag {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: #FFFFFF;
  color: #00A89F;
  font-weight: 700;
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  border-radius: 999px;
  padding: 0.35rem 0.8rem;
  margin-bottom: 1rem;
  box-shadow: 0 8px 18px rgba(16,24,39,0.06);
}
.section-title {
  font-weight: 800;
  font-size: 1.9rem;
  color: #0277BD;
  text-transform: lowercase;
  max-width: 480px;
}
.section-text {
  color: #5B6472;
  font-size: 1rem;
  line-height: 1.7;
  max-width: 480px;
}
.promo-list {
  list-style: none;
  padding: 0;
  margin: 0;
  max-width: 480px;
}
.promo-list li {
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  color: #101827;
  font-size: 0.95rem;
  font-weight: 600;
  padding: 0.6rem 0;
  border-top: 1px solid rgba(16,24,39,0.08);
}
.promo-list li:first-child {
  border-top: none;
}
.promo-list-item-link {
  cursor: pointer;
}
.promo-list-item-link:hover {
  color: #0277BD;
}
.plan-tier-cta {
  display: inline-flex;
  align-items: center;
  margin-top: 1.75rem;
  padding: 0.7rem 1.4rem;
  border: none;
  border-radius: 999px;
  background: linear-gradient(90deg, #00BDB3 0%, #0277BD 100%);
  color: #FFFFFF;
  font-family: 'Montserrat', sans-serif;
  font-weight: 700;
  font-size: 0.9rem;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  box-shadow: 0 8px 18px rgba(2, 119, 189, 0.28);
}
.plan-tier-cta:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 24px rgba(2, 119, 189, 0.38);
}
.plan-tier-cta:active {
  transform: translateY(0) scale(0.97);
}

/*
 * `<SubscriptionAndPlans>` é um componente compartilhado, usado em várias
 * telas logadas do produto (painel, config, etc.) — não editamos o arquivo
 * em si para não afetar essas outras telas. Aqui só reskinamos visualmente,
 * via ::v-deep, exatamente como feito na home, pra bater com a paleta,
 * tipografia e raio do resto do produto.
 */
.plans-skin ::v-deep * {
  font-family: 'Montserrat', sans-serif;
}
.plans-skin ::v-deep .header-title.primary--text,
.plans-skin ::v-deep .header-title {
  color: #101827 !important;
}
.plans-skin ::v-deep .v-tabs .tab-item-subscrible-year {
  background-color: #FFFFFF !important;
  color: #0277BD !important;
}
.plans-skin ::v-deep .v-tabs .color-green-blue {
  color: rgba(255,255,255,0.85) !important;
}
.plans-skin ::v-deep .v-card-title .color-green-blue {
  color: #0277BD !important;
}
.plans-skin ::v-deep .primary--text {
  color: #0277BD !important;
}
.plans-skin ::v-deep .v-card.primary {
  background-color: #0277BD !important;
  border-color: #0277BD !important;
}
.plans-skin ::v-deep .v-card {
  border-radius: 1.25rem !important;
}
.plans-skin ::v-deep .v-sheet--outlined {
  border-color: rgba(16,24,39,0.08) !important;
}
.plans-skin ::v-deep .VueCarousel-slide {
  display: flex;
  align-items: stretch;
}
.plans-skin ::v-deep .VueCarousel-slide > .v-card {
  height: 100%;
  display: flex;
  flex-direction: column;
}

/* Escala de cor dos cards de plano, do mais barato ao mais caro — mesma
 * lógica usada na home: branco (gratuito) → azul claro (básico) → azul
 * primário escuro (profissional e empresarial). */
.plans-skin ::v-deep .VueCarousel-slide:nth-child(2) .v-sheet--outlined {
  background-color: #E3F1FA !important;
}
.plans-skin ::v-deep .VueCarousel-slide:nth-child(2) .v-sheet--outlined .v-list {
  background-color: transparent !important;
}
.plans-skin ::v-deep .VueCarousel-slide:nth-child(3) .v-sheet--outlined,
.plans-skin ::v-deep .VueCarousel-slide:last-child .v-sheet--outlined {
  background-color: #0277BD !important;
  border-color: #0277BD !important;
}
.plans-skin ::v-deep .VueCarousel-slide:nth-child(3) .v-sheet--outlined,
.plans-skin ::v-deep .VueCarousel-slide:nth-child(3) .v-sheet--outlined *,
.plans-skin ::v-deep .VueCarousel-slide:last-child .v-sheet--outlined,
.plans-skin ::v-deep .VueCarousel-slide:last-child .v-sheet--outlined * {
  color: #FFFFFF !important;
}
.plans-skin ::v-deep .VueCarousel-slide:nth-child(3) .v-sheet--outlined .v-card.primary,
.plans-skin ::v-deep .VueCarousel-slide:nth-child(3) .v-sheet--outlined .v-card.primary *,
.plans-skin ::v-deep .VueCarousel-slide:last-child .v-sheet--outlined .v-card.primary,
.plans-skin ::v-deep .VueCarousel-slide:last-child .v-sheet--outlined .v-card.primary * {
  background-color: #FFFFFF !important;
  color: #0277BD !important;
}
.plans-skin ::v-deep .VueCarousel-slide:nth-child(3) .v-sheet--outlined .v-list,
.plans-skin ::v-deep .VueCarousel-slide:last-child .v-sheet--outlined .v-list {
  background-color: transparent !important;
}

@media (max-width: 960px) {
  .section-title {
    font-size: 1.5rem;
  }
  .plan-tier-mock {
    max-width: 100%;
  }
}
</style>
